import { useState } from "react";
import { Lock, PowerOff, Power, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useFleet } from "@/context/FleetContext";
import { vehicleStatus } from "@/lib/fleet";
import { useAuth } from "@/context/AuthContext";

export function EngineCommand({ vehicle }) {
  const [open, setOpen] = useState(null);
  const [pw, setPw] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const { bumpCommands } = useFleet();
  const { isDemo } = useAuth();
  const moving = vehicleStatus(vehicle) === "moving";

  // Pengaman eksplisit: demo tidak menyediakan kontrol mesin.
  if (isDemo) {
    return (
      <div
        className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground"
        data-testid="engine-command-demo-disabled"
      >
        <div className="font-bold">Kontrol Mesin — Mode Demo</div>
        <p className="mt-1">
          Perintah engine cut-off dan pemulihan mesin dinonaktifkan.
          Tidak ada perintah yang dikirim ke perangkat GPS.
        </p>
      </div>
    );
  }

  if (!vehicle.supports_cutoff) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="engine-command-unsupported">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Perangkat / protokol GPS kendaraan ini tidak mendukung perintah engine cut-off. Tombol perintah dinonaktifkan demi keamanan.</span>
      </div>
    );
  }

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vehicles/${vehicle.id}/command`, { command: open, password: pw });
      toast.success("Perintah dikirim ke perangkat", { description: `Status: ${data.status}. Menunggu konfirmasi perangkat.` });
      setOpen(null);
      bumpCommands();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const start = (cmd) => {
    setPw("");
    setAgree(false);
    setOpen(cmd);
  };

  return (
    <div className="rounded-xl border border-red-500/25 bg-red-500/[0.04] p-3" data-testid="engine-command-card">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-500"><ShieldAlert className="h-4 w-4" /> Kontrol Mesin</div>
      <div className="mb-3 text-xs text-muted-foreground">
        Status: <b className="text-foreground" data-testid="engine-state">{vehicle.immobilized ? "Mesin dimatikan" : "Normal"}</b>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" className="border-red-500/40 text-red-500 hover:bg-red-500/10" onClick={() => start("engine_cut")} data-testid="engine-cut-btn">
          <PowerOff className="mr-1.5 h-4 w-4" /> Cut-off
        </Button>
        <Button variant="outline" size="sm" onClick={() => start("engine_restore")} data-testid="engine-restore-btn">
          <Power className="mr-1.5 h-4 w-4" /> Pulihkan
        </Button>
      </div>
      <AlertDialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <AlertDialogContent className="border-border bg-popover" data-testid="engine-command-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{open === "engine_cut" ? "Konfirmasi Engine Cut-off" : "Konfirmasi Pemulihan Mesin"}</AlertDialogTitle>
            <AlertDialogDescription>
              {vehicle.name} · <span className="font-mono">{vehicle.plate}</span> · IMEI <span className="font-mono">{vehicle.imei}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {open === "engine_cut" && (
            <div className={`rounded-xl border p-3 text-sm ${moving ? "border-red-500/50 bg-red-500/10 text-red-500" : "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"}`} data-testid="engine-cut-warning">
              {moving
                ? `PERINGATAN: kendaraan sedang bergerak ${Math.round(vehicle.speed)} km/j. Mematikan mesin saat berjalan dapat membahayakan pengemudi. Disarankan menunggu kendaraan berhenti.`
                : "Mesin akan dimatikan melalui relay perangkat GPS. Pastikan kendaraan dalam kondisi aman."}
            </div>
          )}
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-yellow-500" data-testid="engine-command-agree" />
            <span>Saya memahami risiko dan bertanggung jawab atas perintah ini. Tindakan dicatat di audit log.</span>
          </label>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Masukkan password akun Anda" className="gt-input" data-testid="engine-command-password" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="engine-command-cancel">Batal</AlertDialogCancel>
            <Button disabled={!agree || !pw || busy} onClick={send} className={open === "engine_cut" ? "bg-red-600 text-white hover:bg-red-700" : ""} data-testid="engine-command-confirm">
              {busy ? "Mengirim..." : "Kirim Perintah"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
